"use client";

import { Fragment } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FaWhatsapp } from "react-icons/fa";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import { servicesList } from "@/lib/data";
import { getLocaleFromPathname, getLocalizedPathname } from "@/lib/i18n";
import { getServiceRoute } from "@/lib/serviceUtils";
import { t } from "@/content/i18n/messages";
import { getFullVersionString, getLastUpdatedString } from "@/lib/version";
import { trackEvent } from "@/lib/analytics";
import { floatingWhatsappMessage, whatsappHref } from "@/lib/whatsapp";

const logoSmallImage = "/images/logo.png";

export default function Footer() {
  const pathname = usePathname();
  const currentLocale = getLocaleFromPathname(pathname);
  const localizedHref = (href: string) => getLocalizedPathname(href, currentLocale);
  const brandName = t(currentLocale, "Anchor Sprint");
  const translate = (text: string) => t(currentLocale, text);

  return (
    <footer className="bg-background pb-12 text-neutral-emphasis">
      {/* Hairline bookending the page */}
      <div className="h-px w-full bg-border" aria-hidden />
      <div className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8">
        {/* Talk AI mini-CTA — light card so the page-level dark CTA stays the single dark closing moment */}
        <div className="mb-12 rounded-3xl border border-border bg-card p-6 sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight text-neutral-emphasis sm:text-3xl">
                {translate("Talk AI with us")}
              </h2>
              <p className="mt-2 max-w-xl text-sm text-neutral-muted">
                {translate("Quick question or full roadmap, our team replies within one business day.")}
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <a
                href={whatsappHref(floatingWhatsappMessage(pathname))}
                target="_blank"
                rel="noopener noreferrer"
                data-track-event="contact_whatsapp_click"
                data-track-location="footer_cta"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#20BD5A] sm:w-auto"
              >
                <FaWhatsapp className="h-4 w-4" aria-hidden />
                {translate("Chat on WhatsApp")}
              </a>
              <Link
                href={localizedHref("/contact/")}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold text-neutral-emphasis transition hover:border-neutral-border hover:bg-neutral-surface sm:w-auto"
              >
                {translate("Contact Us")}
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-5">
          <div>
            <div className="flex items-center mb-4">
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                <img
                  src={logoSmallImage}
                  alt={`${brandName} Logo`}
                  className="h-8 w-8 rounded-lg object-contain"
                />
              </div>
              <span className="ml-2 font-medium text-neutral-emphasis">{brandName}</span>
            </div>
            <p className="mb-4 text-sm text-neutral-muted">
              {translate("Delivering tailored technology solutions for seamless transformation, enhanced performance, and lasting growth.")}
            </p>
            <p className="mb-4 text-xs font-medium text-neutral-muted">
              <Link
                href={localizedHref("/partners/anthropic-claude-partner/")}
                className="transition-colors hover:text-primary"
              >
                {/* Approved partner wording: English on every locale. */}
                Certified in the Claude Partner Network
              </Link>
            </p>
            <p className="-mt-3 mb-4 text-xs text-neutral-muted">
              {translate("Team includes Claude Certified Architects")}
            </p>
            <div className="flex space-x-3">
              <a
                href="https://www.facebook.com/people/Anchor-Sprint/61569209893231/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-neutral-muted transition hover:border-neutral-border hover:text-neutral-emphasis"
              >
                <Facebook className="h-4 w-4" aria-hidden />
              </a>
              <a
                href="https://www.linkedin.com/company/anchor-sprint-sdn-bhd/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-neutral-muted transition hover:border-neutral-border hover:text-neutral-emphasis"
              >
                <Linkedin className="h-4 w-4" aria-hidden />
              </a>
              <a
                href="https://www.instagram.com/anchorsprint/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border text-neutral-muted transition hover:border-neutral-border hover:text-neutral-emphasis"
              >
                <Instagram className="h-4 w-4" aria-hidden />
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">{translate("Services")}</h3>
            <ul className="space-y-2">
              {servicesList
                .filter((service) => service.detailId !== "te")
                .map((service, index) => (
                  <Fragment key={index}>
                    <li>
                      <Link
                        href={localizedHref(getServiceRoute(service.detailId))}
                        className="text-neutral-muted transition-colors hover:text-primary"
                      >
                        {translate(service.title)}
                      </Link>
                    </li>
                    {/* AI segment pages grouped directly under the AI Solutions overview */}
                    {service.detailId === "as" && (
                      <>
                        <li>
                          <Link
                            href={localizedHref(
                              "/services/claude-managed-service-malaysia/",
                            )}
                            className="text-neutral-muted transition-colors hover:text-primary"
                          >
                            {translate("Claude Managed Service")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            href={localizedHref("/services/ai-agents/")}
                            className="text-neutral-muted transition-colors hover:text-primary"
                          >
                            {translate("AI Agent Development")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            href={localizedHref("/services/training-education/")}
                            className="text-neutral-muted transition-colors hover:text-primary"
                          >
                            {translate("Claude & AI Training")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            href={localizedHref("/academy/")}
                            className="text-neutral-muted transition-colors hover:text-primary"
                          >
                            {translate("Anchor Academy")}
                          </Link>
                        </li>
                        <li>
                          <Link
                            href={localizedHref("/services/conversational-ai/")}
                            className="text-neutral-muted transition-colors hover:text-primary"
                          >
                            {translate("Conversational AI")}
                          </Link>
                        </li>
                      </>
                    )}
                  </Fragment>
                ))}
              <li>
                <Link
                  href={localizedHref("/services/ai-consulting-malaysia/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("AI Consulting")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/services/seo-services-malaysia/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("AI SEO Agency Malaysia")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/services/ai-seo-kuala-lumpur/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("AI SEO Kuala Lumpur")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/services/vibe-code-rescue/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Vibe Code Rescue")}
                </Link>
              </li>
            </ul>
          </div>


          <div>
            <h3 className="text-lg font-semibold mb-4">{translate("Enterprise Solutions")}</h3>
            <ul className="space-y-3">
              <li>
                <Link href={localizedHref("/enterprise/")} className="text-neutral-muted transition-colors hover:text-primary">
                  {translate("Enterprise Directory")}
                </Link>
              </li>
              <li>
                <Link href={localizedHref("/enterprise/enquiry/")} className="text-neutral-muted transition-colors hover:text-primary">
                  {translate("Request a Quote")}
                </Link>
              </li>
              <li>
                <Link href={localizedHref("/enterprise/adobe-malaysia/")} className="text-neutral-muted transition-colors hover:text-primary">
                  Adobe Malaysia
                </Link>
              </li>
              <li>
                <Link href={localizedHref("/enterprise/nvidia-malaysia/")} className="text-neutral-muted transition-colors hover:text-primary">
                  NVIDIA Malaysia
                </Link>
              </li>
              <li>
                <Link href={localizedHref("/enterprise/#brands")} className="text-neutral-muted transition-colors hover:text-primary">
                  {translate("All brands")}
                </Link>
              </li>
            </ul>
          </div>
          {/* Private LLM: the GB10 machines grouped by what a buyer is actually
              trying to do — run a model on their own hardware — rather than by
              vendor. Every model page sits one click from any page on the site. */}
          <div>
            <h3 className="text-lg font-semibold mb-4">{translate("Private LLM")}</h3>
            <ul className="space-y-3">
              <li>
                <Link href={localizedHref("/enterprise/dgx-spark-malaysia/")} className="text-neutral-muted transition-colors hover:text-primary">
                  NVIDIA DGX Spark
                </Link>
              </li>
              <li>
                <Link href={localizedHref("/enterprise/asus-ascent-gx10-malaysia/")} className="text-neutral-muted transition-colors hover:text-primary">
                  ASUS Ascent GX10
                </Link>
              </li>
              <li>
                <Link href={localizedHref("/enterprise/dell-pro-max-gb10-malaysia/")} className="text-neutral-muted transition-colors hover:text-primary">
                  Dell Pro Max GB10
                </Link>
              </li>
              <li>
                <Link href={localizedHref("/enterprise/hp-zgx-nano-malaysia/")} className="text-neutral-muted transition-colors hover:text-primary">
                  HP ZGX Nano
                </Link>
              </li>
              <li>
                <Link href={localizedHref("/enterprise/lenovo-thinkstation-pgx-malaysia/")} className="text-neutral-muted transition-colors hover:text-primary">
                  Lenovo ThinkStation PGX
                </Link>
              </li>
              <li>
                <Link href={localizedHref("/enterprise/acer-veriton-gn100-malaysia/")} className="text-neutral-muted transition-colors hover:text-primary">
                  Acer Veriton GN100
                </Link>
              </li>
              <li>
                <Link href={localizedHref("/enterprise/dgx-spark-malaysia/enquiry/")} className="text-neutral-muted transition-colors hover:text-primary">
                  {translate("Request a quotation")}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">{translate("Company")}</h3>
            <ul className="space-y-2">
              <li>
                <Link
                  href={localizedHref("/blog/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Blog")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/ai-governance/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("AI governance")}
                </Link>
              </li>
              <li>
                <a
                  href="https://demo.anchorsprint.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Live Website Demos")}
                </a>
              </li>
              <li>
                <Link
                  href={localizedHref("/partners/anthropic-claude-partner/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Claude partner in Malaysia")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/partners/openai-partner/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("OpenAI partner in Malaysia")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/about-us/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("About Us")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/our-people/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Our People")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/corporate-values/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Corporate Values")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/corporate-mission/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Corporate Mission")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/industries-we-serve/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Industries We Serve")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/our-partners/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Our Partners")}
                </Link>
              </li>
              <li>
                <Link
                  href={localizedHref("/anthropic-claude-partner-malaysia-singapore/")}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Why we choose Claude")}
                </Link>
              </li>
              <li>
                <a
                  href="https://aideploy.my/anthropic-claude-partner-network-malaysia/"
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("AI Deploy Malaysia")}
                </a>
              </li>
              <li>
                <a
                  href="https://aideploy.sg/anthropic-claude-partner-network-singapore/"
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("AI Deploy Singapore")}
                </a>
              </li>
              <li>
                {/* Our own product (free, open source): followed link, new tab */}
                <a
                  href="https://ezychat.ai/"
                  target="_blank"
                  rel="noopener"
                  onClick={() => trackEvent("ezychat_click", { location: "footer" })}
                  className="text-neutral-muted transition-colors hover:text-primary"
                >
                  {translate("Free WhatsApp team inbox → EzyChat")}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact spans the full row. The grid is four columns and the link
              lists already fill it, so left as a fifth child this wrapped onto
              its own line and sat in a single narrow column with the rest of
              the row empty. Full width, laid out horizontally, uses the space. */}
          <div className="md:col-span-2 lg:col-span-5 md:border-t md:border-border md:pt-8">
            <h3 className="text-lg font-semibold mb-4">{translate("Contact")}</h3>
            <ul className="grid gap-x-8 gap-y-4 text-neutral-muted sm:grid-cols-2 lg:grid-cols-4">
              <li className="flex items-start" data-i18n-skip>
                <i className="fas fa-map-marker-alt mt-1 mr-2"></i>
                <span>
                  <span className="block font-semibold text-neutral-emphasis">
                    Anchor Sprint Malaysia
                  </span>
                  No. 68-1, Jalan Setia Utama AS U13/AS,
                  <br />
                  Setia Alam, Seksyen U13, 40170 Shah Alam, Selangor, Malaysia
                  <br />
                  <span className="text-xs">
                    Anchor Sprint Sdn Bhd · 1535934-D
                  </span>
                </span>
              </li>
              <li className="flex items-start" data-i18n-skip>
                <i className="fas fa-map-marker-alt mt-1 mr-2"></i>
                <span>
                  <span className="block font-semibold text-neutral-emphasis">
                    Anchor Sprint Singapore
                  </span>
                  <Link
                    href="/singapore/claude-partner/"
                    className="transition-colors hover:text-primary"
                  >
                    7 Temasek Boulevard, #12-07 Suntec Tower One,
                    <br />
                    Singapore 038987
                  </Link>
                  <br />
                  <span className="text-xs">
                    Anchor Sprint Pte. Ltd. · UEN 202632732Z
                  </span>
                </span>
              </li>
              <li className="flex items-center" data-i18n-skip>
                <i className="fas fa-phone-alt mr-2"></i>
                <span>+6011 59246128</span>
              </li>
              <li className="flex items-center" data-i18n-skip>
                <i className="fas fa-envelope mr-2"></i>
                <span>general@anchorsprint.com</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-border pt-8 text-center text-sm text-neutral-muted">
          <div className="flex flex-col items-center justify-between space-y-2 sm:flex-row sm:space-y-0">
            <p>
              &copy; {new Date().getFullYear()} {brandName}. {translate("All rights reserved.")}
              <span className="mx-2" aria-hidden>·</span>
              <Link
                href={localizedHref("/privacy-policy/")}
                className="transition-colors hover:text-primary"
              >
                {translate("Privacy Notice")}
              </Link>
            </p>
            <div className="flex flex-col items-center space-y-1 font-mono text-xs text-neutral-muted sm:flex-row sm:space-y-0 sm:space-x-4">
              <span>{getFullVersionString()}</span>
              <span className="hidden sm:inline">•</span>
              <span>{translate("Last updated:")} {getLastUpdatedString()}</span>
            </div>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-neutral-muted">
            {/* Owner-approved wording. The trademark sentence and the approved
                partner line stay in English on every locale (Anthropic's
                messaging is not translated); only the trailing clause is. */}
            Claude and Anthropic are trademarks of Anthropic, PBC. Anchor Sprint is
            Certified in the Claude Partner Network;{" "}
            {translate("this does not imply endorsement of our services.")}
            {/* OpenAI wording (2026-10-08): text only, never a logo or badge in the
                footer; status exactly "OpenAI Select Partner". */}{" "}
            OpenAI and ChatGPT are trademarks of OpenAI. Anchor Sprint is an{" "}
            OpenAI Select Partner;{" "}
            {translate("this does not imply endorsement of our services.")}
          </p>
        </div>
      </div>
    </footer>
  );
}
